// Enhanced lightbox with keyboard navigation and improved UX
function createLightbox(selector) {
  if (!selector) {
    return console.error("Missing selector argument");
  }

  const images = [...document.querySelectorAll(selector)];
  if (images.length === 0) return;

  let currentIndex = 0;
  const dialogs = [];

  // Create styles for the lightbox
  const style = document.createElement("style");
  style.textContent = `
    .lightbox-button {
      display: inline-block;
      cursor: zoom-in;
      border: none;
      background: none;
      padding: 0;
      margin: 0;
    }
    
    .lightbox {
      position: fixed;
      inset: 0;
      max-width: 100vw;
      max-height: 100vh;
      width: 100%;
      height: 100%;
      padding: 1rem;
      background: rgba(0, 0, 0, 0.9);
      border: none;
      overflow: auto;
      animation: lightbox-fade-in 0.3s ease-out;
    }
    
    @keyframes lightbox-fade-in {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    
    .lightbox::backdrop {
      background: rgba(0, 0, 0, 0.9);
    }
    
    .lightbox-content {
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-height: 100%;
      gap: 1rem;
    }
    
    .lightbox-image-container {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      flex: 1;
    }
    
    .lightbox img {
      max-width: 100%;
      max-height: calc(100vh - 4rem);
      height: auto;
      width: auto;
      object-fit: contain;
      cursor: default;
    }
    
    .lightbox-caption {
      color: #fff;
      text-align: center;
      max-width: 800px;
      font-size: 0.875rem;
      line-height: 1.5;
      padding: 0 2rem;
      margin-bottom: 1rem;
    }
    
    .lightbox-close {
      position: absolute;
      top: 1.5rem;
      right: 1.5rem;
      background: rgba(0, 0, 0, 0.5);
      color: #fff;
      border: none;
      padding: 0.25rem 0.5rem;
      font-size: 1.5rem;
      line-height: 1;
      cursor: pointer;
      border-radius: 4px;
      transition: background-color 0.2s;
      z-index: 10;
    }
    
    .lightbox-close:hover {
      background: rgba(0, 0, 0, 0.8);
    }
    
    .lightbox-nav {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      background: rgba(0, 0, 0, 0.5);
      color: #fff;
      border: none;
      padding: 0.5rem 0.375rem;
      font-size: 1.5rem;
      cursor: pointer;
      border-radius: 4px;
      transition: background-color 0.2s;
      line-height: 1;
    }
    
    .lightbox-nav:hover:not(:disabled) {
      background: rgba(0, 0, 0, 0.8);
    }
    
    .lightbox-nav:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }
    
    .lightbox-nav-prev {
      left: 1.5rem;
    }
    
    .lightbox-nav-next {
      right: 1.5rem;
    }
    
    .lightbox-counter {
      padding-bottom: 1rem;
      left: 50%;
      color: #fff;
      font-size: 0.875rem;
      background: rgba(0, 0, 0, 0.5);
      border-radius: 4px;
    }

    .lightbox-close-icon {
      width: 1.5rem;
      height: 1.5rem;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    @media (max-width: 768px) {
      .lightbox {
        padding: 0.5rem;
      }
      
      .lightbox-nav {
        padding: 0.25rem;
        font-size: 1.25rem;
      }
      
      .lightbox-close {
        padding: 0.125rem 0.25rem;
        font-size: 1.25rem;
      }
    }
  `;
  document.head.appendChild(style);

  const buttonTemplate = document.createElement("button");
  buttonTemplate.classList.add("lightbox-button");
  buttonTemplate.setAttribute("aria-haspopup", "dialog");

  const dialogTemplate = document.createElement("dialog");
  dialogTemplate.classList.add("lightbox");
  dialogTemplate.innerHTML = `
    <div class="lightbox-content">
      <button type="button" class="lightbox-close" aria-label="Close lightbox">
        <div class="lightbox-close-icon" aria-hidden="true">×</div>
      </button>
      <button type="button" class="lightbox-nav lightbox-nav-prev" aria-label="Previous image">
        <div class="lightbox-nav-icon" aria-hidden="true">‹</div>
      </button>
      <button type="button" class="lightbox-nav lightbox-nav-next" aria-label="Next image">
        <div class="lightbox-nav-icon" aria-hidden="true">›</div>
      </button>
      <div style="flex: 1"></div>
      <div class="lightbox-image-container">
        <img alt="">
      </div>
      <div class="lightbox-caption" aria-live="polite"></div>
      <div style="flex: 1"></div>
      <div class="lightbox-counter" aria-live="polite"></div>
    </div>
  `;

  function updateDialog(dialog, index) {
    const img = images[index];
    const dialogImg = dialog.querySelector(".lightbox-image-container img");
    const caption = dialog.querySelector(".lightbox-caption");
    const counter = dialog.querySelector(".lightbox-counter");
    const prevBtn = dialog.querySelector(".lightbox-nav-prev");
    const nextBtn = dialog.querySelector(".lightbox-nav-next");

    dialogImg.src = img.src;
    dialogImg.alt = img.alt || "";
    caption.textContent = img.alt || "";

    if (images.length > 1) {
      counter.textContent = `${index + 1} / ${images.length}`;
      prevBtn.disabled = index === 0;
      nextBtn.disabled = index === images.length - 1;
      prevBtn.style.display = "block";
      nextBtn.style.display = "block";
      counter.style.display = "block";
    } else {
      prevBtn.style.display = "none";
      nextBtn.style.display = "none";
      counter.style.display = "none";
    }
  }

  function navigateTo(dialog, newIndex) {
    if (newIndex >= 0 && newIndex < images.length) {
      currentIndex = newIndex;
      updateDialog(dialog, currentIndex);
    }
  }

  function createDialog(img, index) {
    const button = buttonTemplate.cloneNode();
    const dialog = dialogTemplate.cloneNode(true);
    dialogs.push(dialog);
    // Wrap image in button
    img.before(button);
    button.append(img);
    button.after(dialog);

    // Event handlers
    button.addEventListener("click", () => {
      currentIndex = index;
      updateDialog(dialog, currentIndex);
      dialog.showModal();
    });

    const closeBtn = dialog.querySelector(".lightbox-close");
    closeBtn.addEventListener("click", () => dialog.close());

    const prevBtn = dialog.querySelector(".lightbox-nav-prev");
    const nextBtn = dialog.querySelector(".lightbox-nav-next");

    prevBtn.addEventListener("click", () =>
      navigateTo(dialog, currentIndex - 1)
    );
    nextBtn.addEventListener("click", () =>
      navigateTo(dialog, currentIndex + 1)
    );

    // Click outside to close
    dialog.addEventListener("click", (event) => {
      if (
        event.target === dialog ||
        event.target.classList.contains("lightbox-content")
      ) {
        dialog.close();
      }
    });

    // Keyboard navigation
    dialog.addEventListener("keydown", (event) => {
      switch (event.key) {
        case "ArrowLeft":
          navigateTo(dialog, currentIndex - 1);
          event.preventDefault();
          break;
        case "ArrowRight":
          navigateTo(dialog, currentIndex + 1);
          event.preventDefault();
          break;
        case "Escape":
          dialog.close();
          break;
      }
    });
  }

  images.forEach((img, index) => createDialog(img, index));
}

// Initialize lightbox
createLightbox(
  ".email-body-content img:not(.link-image):not(.bandcamp__thumbnail)"
);
